import evidenceEnumsJson from "@/configs/evidence-enums.json";
import evidenceLabelsJson from "@/configs/evidence-labels.json";
import type {
  AggregatedProjectData,
  ContactTopic,
  EvidenceSource,
} from "@/types/aicheck";

interface EvidenceLabelsConfig {
  root_labels: Record<string, string>;
  segment_labels: Record<string, string>;
  item_type_labels: Record<string, string>;
  path_aliases: Record<string, string>;
}

interface EvidenceEnumsConfig {
  field_enums: Record<string, Record<string, string>>;
  special_values: Record<string, string>;
}

interface ItineraryDayRef {
  dayIndex: number;
  dayId?: number;
  dayNumber?: number;
  itinerary: Record<string, unknown>;
}

interface ItineraryItemRef {
  dayIndex: number;
  itemIndex: number;
  dayId?: number;
  dayNumber?: number;
  itemId?: number;
  itemType?: string;
  itemName?: string;
  item: Record<string, unknown>;
}

interface EvidenceLabelIndexes {
  itineraryDaysByIndex: ItineraryDayRef[];
  itineraryDaysById: Map<number, ItineraryDayRef>;
  itineraryDaysByNumber: Map<number, ItineraryDayRef>;
  itineraryItemsByDayAndIndex: Map<string, ItineraryItemRef>;
  itineraryItemsById: Map<number, ItineraryItemRef>;
  projectContactsById: Map<number, Record<string, unknown>>;
}

export interface FormattedEvidenceLabel {
  display: string;
  raw: string | null;
}

export interface EvidenceLabelResolver {
  formatSource: (source: string) => FormattedEvidenceLabel;
  formatEvidenceText: (value: string) => string;
  formatQuote: (quote: string, source?: string) => string;
  formatEnumValue: (fieldKey: string, value: string) => string;
  formatContactType: (value: string) => string;
  formatContactPurpose: (value: string) => string;
  formatContactSourceSummary: (source: EvidenceSource) => string;
}

const LABELS = evidenceLabelsJson as EvidenceLabelsConfig;
const ENUMS = evidenceEnumsJson as EvidenceEnumsConfig;

export function createEvidenceLabelResolver({
  projectData,
  contactTopics,
}: {
  projectData?: AggregatedProjectData | null;
  contactTopics?: ContactTopic[] | null;
}): EvidenceLabelResolver {
  const indexes = buildIndexes(projectData);
  const topics = contactTopics ?? [];

  const formatEnumValue = (fieldKey: string, value: string) =>
    lookupEnumValue(fieldKey, value);

  const formatQuote = (quote: string, source?: string) =>
    formatScalarValue(quote, source);

  const formatSource = (source: string): FormattedEvidenceLabel => {
    const normalized = normalizeSource(source);
    const display =
      formatContactQuotePath(normalized, topics, indexes) ??
      formatPseudoItinerarySource(normalized, indexes) ??
      formatArrowSource(normalized, indexes) ??
      formatDotPath(normalized, indexes) ??
      normalized;

    return {
      display,
      raw: display === source ? null : source,
    };
  };

  const formatEvidenceText = (value: string) => {
    let formatted = replaceBulletEvidenceLines(
      value,
      formatSource,
      formatQuote,
    );
    formatted = replaceQuotedPathValuePairs(formatted, formatSource);
    return formatted;
  };

  const formatContactType = (value: string) =>
    lookupEnumValue("contact_type", value);
  const formatContactPurpose = (value: string) =>
    lookupEnumValue("contact_purpose", value);
  const formatContactSourceSummary = (source: EvidenceSource) => {
    const parts = ["Contact History"];
    parts.push(formatContactType(source.source_type));

    const dateLabel = formatDateLabel(source.contacted_at);
    if (dateLabel) parts.push(dateLabel);

    if (source.contact_purpose) {
      parts.push(formatContactPurpose(source.contact_purpose));
    }

    return parts.join(" > ");
  };

  return {
    formatSource,
    formatEvidenceText,
    formatQuote,
    formatEnumValue,
    formatContactType,
    formatContactPurpose,
    formatContactSourceSummary,
  };
}

function buildIndexes(
  projectData?: AggregatedProjectData | null,
): EvidenceLabelIndexes {
  const itineraryDaysByIndex: ItineraryDayRef[] = [];
  const itineraryDaysById = new Map<number, ItineraryDayRef>();
  const itineraryDaysByNumber = new Map<number, ItineraryDayRef>();
  const itineraryItemsByDayAndIndex = new Map<string, ItineraryItemRef>();
  const itineraryItemsById = new Map<number, ItineraryItemRef>();
  const projectContactsById = new Map<number, Record<string, unknown>>();

  for (const [dayIndex, itinerary] of (
    projectData?.itineraries ?? []
  ).entries()) {
    const itineraryRecord = itinerary as unknown as Record<string, unknown>;
    const itineraryDay = asRecord(itineraryRecord.itinerary_day);
    const dayId = asNumber(itineraryDay?.id);
    const dayNumber = asNumber(itineraryDay?.day_number);
    const dayRef: ItineraryDayRef = {
      dayIndex,
      dayId,
      dayNumber,
      itinerary: itineraryRecord,
    };
    itineraryDaysByIndex.push(dayRef);
    if (dayId != null) itineraryDaysById.set(dayId, dayRef);
    if (dayNumber != null) itineraryDaysByNumber.set(dayNumber, dayRef);

    const items = Array.isArray(itineraryRecord.itinerary_items)
      ? (itineraryRecord.itinerary_items as Record<string, unknown>[])
      : [];
    items.forEach((item, itemIndex) => {
      const itemId = asNumber(item.id);
      const itemType = asString(item.itinerary_itemable_type);
      const itemRef: ItineraryItemRef = {
        dayIndex,
        itemIndex,
        dayId,
        dayNumber,
        itemId,
        itemType: itemType ?? undefined,
        itemName: extractItemName(item),
        item,
      };
      itineraryItemsByDayAndIndex.set(`${dayIndex}:${itemIndex}`, itemRef);
      if (itemId != null) itineraryItemsById.set(itemId, itemRef);
    });
  }

  const projectContacts = Array.isArray(projectData?.project?.project_contacts)
    ? (projectData?.project.project_contacts as Record<string, unknown>[])
    : [];
  for (const contact of projectContacts) {
    const id = asNumber(contact.id);
    if (id != null) projectContactsById.set(id, contact);
  }

  return {
    itineraryDaysByIndex,
    itineraryDaysById,
    itineraryDaysByNumber,
    itineraryItemsByDayAndIndex,
    itineraryItemsById,
    projectContactsById,
  };
}

function replaceBulletEvidenceLines(
  value: string,
  formatSource: (source: string) => FormattedEvidenceLabel,
  formatQuote: (quote: string, source?: string) => string,
): string {
  if (!value.includes("- source:")) return value;

  return value
    .split("\n")
    .map((line) => {
      const match = line.match(
        /^(\s*-\s*)source:\s*(.+?)(?:;\s*quote:\s*(.+))?$/i,
      );
      if (!match) return line;

      const [, prefix, rawSource, rawQuote] = match;
      const sourceLabel = formatSource(rawSource.trim()).display;
      if (!rawQuote) {
        return `${prefix}Source: ${sourceLabel}`;
      }

      return `${prefix}Source: ${sourceLabel}; Quote: ${formatQuote(rawQuote.trim(), rawSource.trim())}`;
    })
    .join("\n");
}

function replaceQuotedPathValuePairs(
  value: string,
  formatSource: (source: string) => FormattedEvidenceLabel,
): string {
  return value.replace(
    /"([^"]+)":\s*("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|null|true|false|-?\d+(?:\.\d+)?)/g,
    (match, rawSource, rawValue) => {
      const formattedSource = formatSource(rawSource);
      const formattedValue = formatScalarValue(rawValue, rawSource);
      const replacement = `${formattedSource.display}: ${formattedValue}`;
      return replacement === match ? match : replacement;
    },
  );
}

function formatContactQuotePath(
  source: string,
  contactTopics: ContactTopic[],
  indexes: EvidenceLabelIndexes,
): string | null {
  const match = source.match(
    /^contact_topics\[(\d+)\]\.evidences\[(\d+)\]\.sources\[(\d+)\](?:\.(\w+))?$/,
  );
  if (!match) return null;

  const topicIndex = Number.parseInt(match[1], 10);
  const evidenceIndex = Number.parseInt(match[2], 10);
  const sourceIndex = Number.parseInt(match[3], 10);
  const fieldKey = match[4];
  const topic = contactTopics[topicIndex];
  const evidence = topic?.evidences[evidenceIndex];
  const contactSource = evidence?.sources[sourceIndex];

  const parts = ["Contact History"];
  if (!contactSource) {
    parts.push(`Topic ${topicIndex + 1}`);
    if (fieldKey) parts.push(labelForSegment(fieldKey));
    return parts.join(" > ");
  }

  parts.push(lookupEnumValue("contact_type", contactSource.source_type));

  const dateLabel = formatDateLabel(contactSource.contacted_at);
  if (dateLabel) parts.push(dateLabel);

  if (contactSource.contact_purpose) {
    parts.push(
      lookupEnumValue("contact_purpose", contactSource.contact_purpose),
    );
  }

  parts.push(topic ? humanizeToken(topic.topic_id) : `Topic ${topicIndex + 1}`);

  const linkedContact = resolveProjectContact(contactSource, indexes);
  const title = asString(linkedContact?.title);
  if (title) {
    parts.push(title);
  }

  if (fieldKey) {
    parts.push(labelForSegment(fieldKey));
  }

  return parts.join(" > ");
}

function formatPseudoItinerarySource(
  source: string,
  indexes: EvidenceLabelIndexes,
): string | null {
  const itineraryDayIdMatch = source.match(/^itinerary_day\[(\d+)\]\.(.+)$/);
  if (itineraryDayIdMatch) {
    const dayId = Number.parseInt(itineraryDayIdMatch[1], 10);
    const ref = indexes.itineraryDaysById.get(dayId);
    const parts = [
      LABELS.root_labels.itineraries ?? "Itinerary",
      formatDayLabel(ref, undefined),
      labelForPathTail(itineraryDayIdMatch[2]),
    ].filter(Boolean);
    return parts.join(" > ");
  }

  const itineraryDayNumberMatch = source.match(
    /^itinerary_day\s+\(day_number\s+(\d+)\)\s*->\s*(.+)$/i,
  );
  if (itineraryDayNumberMatch) {
    const dayNumber = Number.parseInt(itineraryDayNumberMatch[1], 10);
    const ref = indexes.itineraryDaysByNumber.get(dayNumber);
    const parts = [
      LABELS.root_labels.itineraries ?? "Itinerary",
      formatDayLabel(ref, dayNumber),
      labelForPathTail(itineraryDayNumberMatch[2]),
    ].filter(Boolean);
    return parts.join(" > ");
  }

  const itineraryDayMatch = source.match(
    /^itineraries\s+Day\s+([0-9]+(?:\s*&\s*[0-9]+)?(?:-[0-9]+)?)\s+\(id[s]?:[^)]+\)(.*)$/i,
  );
  if (itineraryDayMatch) {
    const dayLabel = itineraryDayMatch[1]
      .split(/\s*&\s*/)
      .map((part) => `Day ${part.trim()}`)
      .join(" & ");
    const suffix = itineraryDayMatch[2].trim();
    const parts = [LABELS.root_labels.itineraries ?? "Itinerary", dayLabel];
    if (suffix) {
      const normalizedSuffix = suffix
        .replace(/^\s*-\s*/, "")
        .replace(/^\s*items$/i, "Items");
      if (/^items$/i.test(normalizedSuffix)) {
        parts.push("Items");
      } else if (normalizedSuffix) {
        parts.push(labelForPathTail(normalizedSuffix));
      }
    }
    return parts.join(" > ");
  }

  return null;
}

function formatArrowSource(
  source: string,
  indexes: EvidenceLabelIndexes,
): string | null {
  if (!source.includes("->")) return null;

  const rawParts = source
    .split("->")
    .map((part) => part.trim())
    .filter(Boolean);
  if (rawParts.length === 0) return null;

  const formattedParts: string[] = [];
  let currentDay: ItineraryDayRef | null = null;

  for (const rawPart of rawParts) {
    if (rawPart === "itineraries") {
      formattedParts.push(LABELS.root_labels.itineraries ?? "Itinerary");
      continue;
    }

    if (rawPart === "profitSummary" || rawPart === "paymentSummary") {
      formattedParts.push(labelForRoot(rawPart));
      continue;
    }

    const dayMatch = rawPart.match(/^day_number\s+(\d+)$/i);
    if (dayMatch) {
      const dayNumber = Number.parseInt(dayMatch[1], 10);
      currentDay = indexes.itineraryDaysByNumber.get(dayNumber) ?? null;
      formattedParts.push(formatDayLabel(currentDay, dayNumber));
      continue;
    }

    const itineraryItemsMatch = rawPart.match(
      /^itinerary_items\s+\(([^)]+)\)(?:\.(.+))?$/i,
    );
    if (itineraryItemsMatch) {
      formattedParts.push(labelForItemType(itineraryItemsMatch[1]));
      if (itineraryItemsMatch[2]) {
        formattedParts.push(labelForPathTail(itineraryItemsMatch[2]));
      }
      continue;
    }

    const profitTypeMatch = rawPart.match(
      /^item_type_profit_summaries\s+\(([^)]+)\)$/i,
    );
    if (profitTypeMatch) {
      formattedParts.push(labelForSegment("item_type_profit_summaries"));
      formattedParts.push(labelForItemType(profitTypeMatch[1]));
      continue;
    }

    formattedParts.push(labelForPathTail(rawPart));
  }

  return formattedParts.join(" > ");
}

function formatDotPath(
  source: string,
  indexes: EvidenceLabelIndexes,
): string | null {
  const segments = source.split(".");
  if (segments.length === 0) return null;

  const firstSegment = parseSegment(segments[0]);
  if (!firstSegment) return null;

  if (
    firstSegment.key !== "customer_profile" &&
    firstSegment.key !== "project" &&
    firstSegment.key !== "itineraries" &&
    firstSegment.key !== "profitSummary" &&
    firstSegment.key !== "paymentSummary" &&
    firstSegment.key !== "contact_topics"
  ) {
    return null;
  }

  if (firstSegment.key === "contact_topics") {
    return (
      formatContactQuotePath(source, [], indexes) ??
      formatGenericDotPath(segments)
    );
  }

  if (firstSegment.key !== "itineraries") {
    return formatGenericDotPath(segments);
  }

  return formatItineraryDotPath(segments, indexes);
}

function formatItineraryDotPath(
  segments: string[],
  indexes: EvidenceLabelIndexes,
): string {
  const root = parseSegment(segments[0]);
  const parts = [LABELS.root_labels.itineraries ?? "Itinerary"];
  const dayRef = resolveItineraryDay(root?.selector, indexes);
  const rootIndex = numericSelector(root?.selector);
  const rootDayLabel =
    dayRef || rootIndex != null
      ? formatDayLabel(dayRef, rootIndex != null ? rootIndex + 1 : undefined)
      : null;
  if (rootDayLabel) {
    parts.push(rootDayLabel);
  }

  let index = 1;
  let itemRef: ItineraryItemRef | null = null;

  while (index < segments.length) {
    const segment = parseSegment(segments[index]);
    if (!segment) {
      index += 1;
      continue;
    }

    if (segment.key === "itinerary_day") {
      index += 1;
      continue;
    }

    if (segment.key === "itinerary_items") {
      itemRef = resolveItineraryItem(segment.selector, dayRef, indexes);
      if (
        !rootDayLabel &&
        itemRef?.dayNumber != null &&
        parts[parts.length - 1] !== `Day ${itemRef.dayNumber}`
      ) {
        parts.push(`Day ${itemRef.dayNumber}`);
      }
      parts.push(formatItemLabel(itemRef, segment.selector));
      index += 1;
      continue;
    }

    if (segment.key === "detail") {
      index += 1;
      continue;
    }

    if (segment.key === "itinerary_arrangement") {
      parts.push(labelForSegment(segment.key));
      index += 1;
      continue;
    }

    const remainingPath = segments.slice(index).join(".");
    parts.push(labelForPathTail(remainingPath));
    break;
  }

  return dedupeParts(parts).join(" > ");
}

function formatGenericDotPath(segments: string[]): string {
  const parts: string[] = [];
  for (const rawSegment of segments) {
    const segment = parseSegment(rawSegment);
    if (!segment) continue;

    parts.push(
      parts.length === 0
        ? labelForRoot(segment.key)
        : labelForSegment(segment.key),
    );

    const selectorNumber = numericSelector(segment.selector);
    if (selectorNumber != null) {
      parts.push(`Item ${selectorNumber + 1}`);
    }
  }
  return dedupeParts(parts).join(" > ");
}

function resolveItineraryDay(
  selector: string | undefined,
  indexes: EvidenceLabelIndexes,
): ItineraryDayRef | null {
  const numeric = numericSelector(selector);
  if (numeric != null) {
    return indexes.itineraryDaysByIndex[numeric] ?? null;
  }
  return null;
}

function resolveItineraryItem(
  selector: string | undefined,
  dayRef: ItineraryDayRef | null,
  indexes: EvidenceLabelIndexes,
): ItineraryItemRef | null {
  if (!selector) return null;

  const idMatch = selector.match(/^id=(\d+)$/);
  if (idMatch) {
    return (
      indexes.itineraryItemsById.get(Number.parseInt(idMatch[1], 10)) ?? null
    );
  }

  const itemIndex = numericSelector(selector);
  if (itemIndex == null || !dayRef) return null;

  return (
    indexes.itineraryItemsByDayAndIndex.get(
      `${dayRef.dayIndex}:${itemIndex}`,
    ) ?? null
  );
}

function resolveProjectContact(
  source: EvidenceSource,
  indexes: EvidenceLabelIndexes,
): Record<string, unknown> | null {
  const match = source.source_id.match(/^contact_(\d+)$/);
  if (!match) return null;

  return indexes.projectContactsById.get(Number.parseInt(match[1], 10)) ?? null;
}

function formatDayLabel(
  ref: ItineraryDayRef | null | undefined,
  fallbackDayNumber?: number,
): string {
  if (ref?.dayNumber != null) return `Day ${ref.dayNumber}`;
  if (fallbackDayNumber != null) return `Day ${fallbackDayNumber}`;
  return "Day";
}

function formatItemLabel(
  itemRef: ItineraryItemRef | null,
  selector?: string,
): string {
  if (!itemRef) {
    const idMatch = selector?.match(/^id=(\d+)$/);
    if (idMatch) return `Item ${idMatch[1]}`;
    const index = numericSelector(selector);
    return index != null ? `Item ${index + 1}` : "Item";
  }

  const typeLabel = labelForItemType(itemRef.itemType);
  if (itemRef.itemName) {
    return `${typeLabel}: ${itemRef.itemName}`;
  }
  if (itemRef.itemId != null) {
    return `${typeLabel} Item ${itemRef.itemId}`;
  }
  return typeLabel;
}

function labelForRoot(key: string): string {
  return LABELS.root_labels[key] ?? labelForSegment(key);
}

function labelForSegment(key: string): string {
  return LABELS.segment_labels[key] ?? humanizeToken(key);
}

function labelForItemType(value?: string): string {
  if (!value) return "Item";
  return (
    LABELS.item_type_labels[value] ??
    lookupEnumValue("itinerary_itemable_type", value)
  );
}

function labelForPathTail(path: string): string {
  const parts = path.split(".").flatMap((rawSegment) => {
    const segment = parseSegment(rawSegment);
    if (!segment) return [humanizeFreeformSegment(rawSegment)];
    if (segment.key === "detail") return [];

    const labels = [labelForSegment(segment.key)];
    const selectorValue = numericSelector(segment.selector);
    if (selectorValue != null) {
      labels.push(`Item ${selectorValue + 1}`);
    }
    return labels;
  });

  return dedupeParts(parts).join(" > ");
}

function lookupEnumValue(fieldKey: string, value: string): string {
  const normalizedValue = stripWrappingQuotes(value).trim();
  if (!normalizedValue) return value;

  const fieldValues = ENUMS.field_enums[fieldKey];
  if (fieldValues?.[normalizedValue]) {
    return fieldValues[normalizedValue];
  }

  if (ENUMS.special_values[normalizedValue]) {
    return ENUMS.special_values[normalizedValue];
  }

  return humanizeToken(normalizedValue);
}

function formatScalarValue(value: string, source?: string): string {
  const trimmed = value.trim();
  const annotatedScalarMatch = trimmed.match(
    /^("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|null|true|false|-?\d+(?:\.\d+)?)([\s\S]*)$/,
  );
  const scalarToken = annotatedScalarMatch?.[1] ?? trimmed;
  const scalarSuffix = annotatedScalarMatch?.[2] ?? "";
  const rawValue = stripWrappingQuotes(scalarToken);
  if (!source || /\s/.test(rawValue)) {
    return trimmed;
  }

  const fieldKey = extractFieldKeyFromSource(source);
  if (!fieldKey) return trimmed;

  return `${lookupEnumValue(fieldKey, rawValue)}${scalarSuffix}`;
}

function normalizeSource(source: string): string {
  let normalized = source.trim();
  for (const [needle, replacement] of Object.entries(LABELS.path_aliases)) {
    const regex = new RegExp(`^${escapeRegExp(needle)}`);
    normalized = normalized.replace(regex, replacement);
  }

  normalized = normalized.replace(/^customer profile\./i, "customer_profile.");
  normalized = normalized.replace(/\s+/g, " ").trim();
  return normalized;
}

function extractFieldKeyFromSource(source: string): string | null {
  const normalized = normalizeSource(source);
  const segments = normalized.split(".");
  const lastSegment = segments.at(-1);
  const parsed = lastSegment ? parseSegment(lastSegment) : null;
  return parsed?.key ?? null;
}

function extractItemName(item: Record<string, unknown>): string | undefined {
  const detail = asRecord(item.detail);
  const itineraryPlanItem = asRecord(detail?.itinerary_plan_item);
  const hotel = asRecord(detail?.hotel);
  return (
    asString(detail?.name) ??
    asString(itineraryPlanItem?.name) ??
    asString(hotel?.name) ??
    undefined
  );
}

function parseSegment(
  value: string,
): { key: string; selector?: string } | null {
  const match = value.match(/^([A-Za-z_][A-Za-z0-9_]*)(?:\[(.*?)\])?$/);
  if (!match) return null;
  return {
    key: match[1],
    selector: match[2],
  };
}

function numericSelector(selector?: string): number | undefined {
  if (!selector || !/^\d+$/.test(selector)) return undefined;
  return Number.parseInt(selector, 10);
}

function formatDateLabel(value: string): string | null {
  if (!value) return null;
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1] ?? value;
}

function humanizeToken(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function humanizeFreeformSegment(value: string): string {
  return value
    .split("/")
    .map((part) => humanizeToken(part.trim()))
    .join(" / ");
}

function stripWrappingQuotes(value: string): string {
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    return value.slice(1, -1);
  }
  return value;
}

function dedupeParts(parts: string[]): string[] {
  return parts.filter((part, index) => {
    if (!part) return false;
    return index === 0 || parts[index - 1] !== part;
  });
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}
